import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password reset successful — please sign in.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err));
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 mb-6">
          <ArrowLeft size={14} />
          Back to sign in
        </Link>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 font-heading">Set a new password</h1>
        <p className="mt-2 text-sm text-slate-600">
          {token
            ? "Choose a new password for your account."
            : "This page requires a valid reset link from your email."}
        </p>

        {!token ? (
          <div className="mt-6 space-y-4" data-testid="reset-no-token">
            <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 px-4 py-3 rounded-md">
              No reset token in URL. Please use the password-reset link from your email.
              If the link doesn't work, request a new one.
            </div>
            <Link
              to="/forgot-password"
              className="inline-block text-sm font-medium text-blue-900 hover:underline"
              data-testid="request-new-link"
            >
              Request a new reset link →
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-new-password-input"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-password-input"
                className="mt-1.5 h-11"
              />
            </div>
            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{error}</div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              data-testid="reset-submit-btn"
              className="w-full h-11 bg-blue-900 hover:bg-blue-800"
            >
              {submitting ? "Resetting…" : "Reset password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
